import * as Dialog from '@radix-ui/react-dialog';
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { useActiveCompanyId } from '@/lib/active-company';
import { useGenerateSnapshot } from '@/lib/snapshots';

const NAME_LIMIT = 120;
const NOTES_LIMIT = 2000;

type Props = {
  trigger: ReactNode;
  onGenerated?: (id: string) => void;
};

export function GenerateSnapshotDialog({ trigger, onGenerated }: Props) {
  const companyId = useActiveCompanyId();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const generate = useGenerateSnapshot(companyId);

  function reset() {
    setName('');
    setNotes('');
    setError(null);
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    try {
      const result = await generate.mutateAsync({
        name: name.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      reset();
      setOpen(false);
      onGenerated?.(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate snapshot.');
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-text-primary/40" />
        <Dialog.Content
          className="fixed left-1/2 top-24 z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2 rounded-lg border border-border bg-surface shadow-card-hover"
        >
          <form onSubmit={onSubmit} className="flex flex-col gap-5 p-7">
            <div className="flex flex-col gap-2">
              <Dialog.Title className="font-display text-h3 font-semibold text-text-primary">
                Generate snapshot
              </Dialog.Title>
              <Dialog.Description className="text-body-sm text-text-secondary">
                Captures an immutable copy of the current compliance posture.
                Optional name and notes help auditors find this report later.
              </Dialog.Description>
            </div>

            <Field
              id="snapshot-name"
              label="Name"
              hint={`Optional · ${name.length}/${NAME_LIMIT}`}
            >
              <Input
                id="snapshot-name"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, NAME_LIMIT))}
                placeholder="e.g. Q1 2026 close"
                maxLength={NAME_LIMIT}
                autoFocus
              />
            </Field>

            <Field
              id="snapshot-notes"
              label="Notes"
              hint={`Optional · ${notes.length}/${NOTES_LIMIT}`}
            >
              <textarea
                id="snapshot-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, NOTES_LIMIT))}
                placeholder="Context for whoever reviews this report later."
                maxLength={NOTES_LIMIT}
                rows={4}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-body text-text-primary placeholder:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              />
            </Field>

            {error ? (
              <p className="text-body-sm text-danger" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" disabled={generate.isPending}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" variant="dark" disabled={generate.isPending}>
                {generate.isPending ? 'Generating…' : 'Generate snapshot'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
